import moment from 'moment-timezone'
import { PatientWithMappingReportsAttributes } from '@/jsonapi/PatientWithMappingReports'
import {
  NUMBER_OF_ONE_DAY_GRID,
  NUMBER_OF_ONE_DAY_INHALER_USAGES_GRID,
  NUMBER_OF_WEEK_DAYS,
  OFFSET_HOURS_TIMESTAMP,
  ONE_DAY_HEIGHT,
  Z_INDEX,
} from './constants'
import { GRADATION_BAR_SERIES, TIME_LABEL_SCATTER_SERIES } from './constants-series'
import { buildInhalerUsagesSeries } from './series-converters/inhaler-usages'
import { buildWalkingDurationSeries } from './series-converters/walking-duration'
import { buildSleepingPeriodSeries } from './series-converters/sleeping-period'
import { buildInterruptionsSeries } from './series-converters/interruptions'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { fillDate } from '@/components/patient-stats/common/utils'
import { chunk } from 'lodash-es'
import { USER_TIME_ZONE } from '@/utils/time'
import SeriesBar = echarts.EChartOption.SeriesBar
import SeriesCustom = echarts.EChartOption.SeriesCustom
import SeriesScatter = echarts.EChartOption.SeriesScatter

export function buildYAxisDate(patientWithMappingReports) {
  const timezone = patientWithMappingReports.timezone || USER_TIME_ZONE
  const yAxisFilledDate = [
    ...fillDate(
      timezone,
      patientWithMappingReports.resultStartTimestamp + OFFSET_HOURS_TIMESTAMP,
      patientWithMappingReports.resultEndTimestamp
    ),
  ]
  const weekOffset = NUMBER_OF_WEEK_DAYS - (moment(yAxisFilledDate[0]).day() - 1)
  return [yAxisFilledDate.slice(0, weekOffset), ...chunk(yAxisFilledDate.slice(weekOffset), NUMBER_OF_WEEK_DAYS)]
    .reduce((acc, cur, idx) => {
      return idx === 0 ? [...acc, ...cur] : [...acc, '', ...cur]
    }, [])
    .reverse()
}
export function buildMappingChartOptions(
  patientWithMappingReports: PatientWithMappingReportsAttributes,
  yAxisDate: number[]
): echarts.EChartOption<SeriesBar | SeriesCustom | SeriesScatter> {
  const timezone = patientWithMappingReports.timezone || USER_TIME_ZONE
  return {
    grid: [
      { x: '7%', y: 20, width: '86%', height: 80 } as echarts.EChartOption.Grid,
      { x: '7%', y: 100, width: '86%', height: ONE_DAY_HEIGHT * yAxisDate.length } as echarts.EChartOption.Grid,
    ],
    tooltip: {
      trigger: 'item',
    },
    yAxis: [
      {
        gridIndex: 0,
        data: ['empty', 'bar'],
        type: 'category',
        axisLine: { show: false },
        axisTick: {
          show: false,
          alignWithLabel: false,
        },
        splitLine: { show: false },
        axisLabel: {
          show: false,
        },
      },
      {
        gridIndex: 1,
        data: yAxisDate.map(y => String(y)),
        type: 'category',
        axisLine: { show: false },
        axisTick: {
          show: false,
          alignWithLabel: true,
        },
        splitLine: { show: true },
        axisLabel: {
          show: true,
          formatter: value => {
            if (!value) {
              return ''
            }
            return moment(parseInt(value, 10))
              .tz(timezone)
              .format(CHART_DATE_FORMAT.DATE)
          },
        },
      },
    ],
    xAxis: [
      {
        // for header
        gridIndex: 0,
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        min: 0,
        max: 24,
      },
      {
        // basic x axis
        gridIndex: 1,
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        min: 0,
        max: NUMBER_OF_ONE_DAY_GRID,
      },
      {
        // for inhaler usage
        gridIndex: 1,
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        min: 0,
        max: NUMBER_OF_ONE_DAY_INHALER_USAGES_GRID,
      },
    ],
    series: [
      {
        ...GRADATION_BAR_SERIES,
        xAxisIndex: 0,
        yAxisIndex: 0,
      },
      {
        ...TIME_LABEL_SCATTER_SERIES,
        xAxisIndex: 0,
        yAxisIndex: 0,
      },
      ...buildInhalerUsagesSeries({
        timezone,
        inhalerUsages: patientWithMappingReports.inhalerUsages,
        options: {
          xAxisIndex: 2,
          yAxisIndex: 1,
          z: Z_INDEX.INHALER_USAGE,
        },
      }),
      buildWalkingDurationSeries({
        timezone,
        yAxis: yAxisDate,
        walkingDuration: patientWithMappingReports.combinedWalkingDuration,
        options: {
          xAxisIndex: 1,
          yAxisIndex: 1,
          z: Z_INDEX.ACTIVITY,
        },
      }),
      buildSleepingPeriodSeries({
        timezone,
        yAxis: yAxisDate,
        sleepingPeriods: patientWithMappingReports.sleepingPeriods,
        options: {
          xAxisIndex: 1,
          yAxisIndex: 1,
          z: Z_INDEX.SLEEP,
        },
      }),
      buildInterruptionsSeries({
        timezone,
        yAxis: yAxisDate,
        majorInterruptions: patientWithMappingReports.majorInterruptions,
        options: {
          xAxisIndex: 1,
          yAxisIndex: 1,
          z: Z_INDEX.INTERRUPTION,
        },
      }),
    ],
  }
}
